import {FC} from 'react';
import type {AxiosError} from 'axios';
import {useSetAuthSecretCode} from '@rocket-ui/lk-api';
import {useUserInteraction} from '@rocket-ui/user-interaction';
import {PasswordInput} from '../PasswordInputForm/PasswordInput';
import {PasswordNumericKeypad} from '../PasswordInputForm/PasswordNumericKeypad';
import {useAutoSubmitCodeLikeInput} from '../useAutoSubmitCodeLikeInput';
import {useIsMaxSm} from '../useIsMaxSm';
import styles from './RecoveryPasswordInputForm.module.css';

interface RecoveryPasswordInputFormProps {
	token: string;
	secretCodeEndpoint?: 'auth' | 'recovery';
	onSubmit: (password: string) => void;
}

const SHAKE_DURATION_MS = 820;
const PIN_LENGTH = 4;

export const RecoveryPasswordInputForm: FC<RecoveryPasswordInputFormProps> = ({
	token,
	secretCodeEndpoint = 'auth',
	onSubmit,
}) => {
	const {notifyUser} = useUserInteraction();
	const {mutateAsync: setAuthSecretCode, isLoading: isAuthLoading} = useSetAuthSecretCode();
	const isLoading = secretCodeEndpoint === 'auth' && isAuthLoading;
	const isMaxSm = useIsMaxSm();

	const {
		value: code,
		onChange: handleChange,
		errorNonce,
		inputRef,
	} = useAutoSubmitCodeLikeInput({
		requiredLength: PIN_LENGTH,
		clearAfterErrorMs: SHAKE_DURATION_MS,
		notifyUser,
		errorMessageFallback: 'Ошибка при вводе пароля',
		getErrorMessage: (err) => (err as AxiosError<{errors?: string[]}> | undefined)?.response?.data?.errors?.[0],
		submit: async (value) => {
			if (secretCodeEndpoint === 'auth') {
				await setAuthSecretCode({token, code: value});
			}

			onSubmit(value);
		},
	});

	return (
		<section className={styles.section}>
			<p className={styles.title}>Придумайте пароль для входа</p>
			<div className={styles.info}>Из любых 4 цифр</div>

			<div className={styles.inputWrap}>
				<PasswordInput
					value={code}
					onChange={handleChange}
					isLoading={isLoading}
					disabled={isLoading}
					ref={inputRef as React.RefObject<HTMLInputElement>}
					errorNonce={errorNonce}
					suppressNativeKeyboard={isMaxSm}
				/>
			</div>

			<PasswordNumericKeypad
				value={code}
				maxLength={PIN_LENGTH}
				disabled={isLoading}
				onDigit={(digit) => handleChange((code + digit).slice(0, PIN_LENGTH))}
				onBackspace={() => handleChange(code.slice(0, -1))}
			/>
		</section>
	);
};
